<template>
  <div class="relative flex flex-col" :class="colSpan">
    <p v-if="field?.display" class="w-full sm:text-md">
      {{ field.display }} {{ !isRequired ? $t('optional') : '' }}
    </p>
    <p v-if="instructions">{{ instructions }}</p>
    <ul
      class="flex flex-1 flex-col flex-wrap space-y-4 py-5"
      :class="{
        'nav:flex-row nav:space-x-10 nav:space-y-0': field?.config?.inline,
      }"
    >
      <li v-for="(option, index) in computedOptions" :key="index">
        <label class="checkbox-container" :for="option.value">
          <span class="sm:text-md">
            {{ option.label }}
          </span>

          <Field
            :id="option.value"
            :value="option.value"
            :name="name || handle || field.handle"
            :label="label || option.label || field.label || field.display"
            :disabled="disabled"
            :rules="rulesAsString"
            type="radio"
          />

          <span class="checkbox-checkmark" />
        </label>
      </li>
    </ul>

    <Transition name="fade">
      <p
        v-if="error"
        class="bottom-0 top-16 text-sm text-red has-bg-img:absolute has-bg-img:bottom-[0.625rem] has-bg-img:line-clamp-1 has-bg-img:px-5 xl:pt-1"
      >
        {{ $t('this_is_required') }}
      </p>
    </Transition>
  </div>
</template>

<script setup>
import { Field } from 'vee-validate'

defineEmits(['update:modelValue'])

const props = defineProps({
  options: {
    type: [Array, Object],
    default: () => [],
  },

  handle: {
    type: String,
    default: null,
  },

  field: {
    type: Object,
    default: null,
  },

  width: {
    type: [Number, String],
    default: null,
  },

  rules: {
    type: [Array, String],
    default: '',
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  label: {
    type: String,
    default: '',
  },

  name: {
    type: String,
    default: null,
  },

  instructions: {
    type: String,
    default: null,
  },

  error: {
    type: String,
    default: null,
  },
})

const rulesAsString = computed(() => {
  if (Array.isArray(props.rules)) {
    return props.rules.join('|')
  }

  return props.rules
})

const isRequired = computed(() => {
  return rulesAsString.value.includes('required')
})

const colSpan = computed(() => {
  const width = parseInt(props.width) || props.field?.width || 100

  switch (width) {
    case 25:
      return 'md:col-span-6 lg:col-span-3 col-span-12'
    case 33:
      return 'md:col-span-6 lg:col-span-4 col-span-12'
    case 50:
      return 'md:col-span-6 lg:col-span-6 col-span-12'
    case 66:
      return 'md:col-span-6 lg:col-span-8 col-span-12'
    default:
      return 'md:col-span-6 lg:col-span-12 col-span-12'
  }
})

const computedOptions = computed(() => {
  if (Array.isArray(props.options)) {
    return props.options.map((option) => {
      if (typeof option === 'string') {
        return {
          label: option,
          value: option,
        }
      }

      return option
    })
  }

  return Object.keys(props.options).map((key) => ({
    label: props.options[key],
    value: key,
  }))
})
</script>

<style scoped>
.checkbox-container {
  @apply block relative pl-8 cursor-pointer select-none;
}

.checkbox-container input {
  @apply absolute opacity-0 cursor-pointer;
}

.checkbox-checkmark {
  @apply absolute mt-1 inset-x-0 top-0 bg-white rounded-full flex items-center justify-center border-2 border-blue-800 transition-all duration-100 ease-in-out;
  width: 1.375rem;
  height: 1.375rem;
}

.checkbox-container input:focus-visible ~ .checkbox-checkmark {
  @apply ring;
}

.checkbox-checkmark:after {
  @apply absolute opacity-0 transition-all duration-150 ease-in-out rounded-full bg-blue-800 w-3 h-3;

  content: '';
}

.checkbox-container input:checked ~ .checkbox-checkmark:after {
  @apply opacity-100;
}

.checkbox-container input:checked ~ .checkbox-checkmark {
  @apply bg-white;
}
</style>
